import React, { useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { isValidSlots } from "utils/Utils";
import { CatelougeContext } from "store/contexts/Catelouge";
import { API } from "api";
import { CONSTANTS as C } from "config";
import { UTILS } from "utils";
import { ACTIONS } from "config";
import { logToSentry } from "utils/Utils";
import { STOTE_UTILS } from "utils";
import { errorHandler } from "api/api/errorHandler";
import AppContainer from "components/ui/AppContainer";
import AppButton from "components/ui/AppButton";
import AppOutlineButton from "components/ui/AppOutlineButton";
import BasicInfo from "components/features/store/create/BasicInfo"
import Location from "components/features/store/create/Location"
import Delivery from "components/features/store/create/Delivery"
import Pickup from "components/features/store/create/Pickup";
import Dinein from "components/features/store/create/Dinin"

const UpdateStoreComponent = ({ footer, storeId = null, as = "page", setCurrentMode, title = "Update Store", subTitle = "Please fill the complete information to Update Store." }) => {
  const router = useHistory();
  const { t } = useTranslation();
  const location = useLocation();

  const { dispatch } = React.useContext(CatelougeContext);
  const { currentLanguage } = useSelector((state) => state.LanguageSlice);
  const [store, setStore] = useState(UTILS.getDumbStoreObject());
  const [loading, setLoading] = useState(false);

  const getStoreInfo = async (sId) => {
    setLoading(true);
    const userDetail = UTILS.getCurrentUser();
    const result = await API.GET_STORE_INFO_WITH_STORE_ID({
      storeId: sId,
      brandId: userDetail.brandId,
      token: userDetail.token,
      language: currentLanguage,
    });
    if (result?.success === 1 && result?.data?.store?.length >= 1) {
      const nextStore = getStoreInformation(result.data);
      if (nextStore) setStore(nextStore);
      else UTILS.errorNotification(t("Could not load store data."));
    } else {
      UTILS.errorNotification(
        errorHandler(result) || t("Could not load store data.")
      );
    }
    setLoading(false);
  };

  const handleStoreMapSection = (key, val) => {
    if (key === C.KEY_MAP_ADDRESS) {
      setStore((prevState) => ({
        ...prevState,
        values: {
          ...prevState.values,
          [C.KEY_MAP_ADDRESS]: val,
        },
        errors: {
          ...prevState.errors,
          [C.KEY_MAP_ADDRESS]: "",
        },
      }));
    } else if (key === "latlng") {
      const lat = val.lat;
      const lng = val.lng;
      setStore((prevState) => ({
        ...prevState,
        values: {
          ...prevState.values,
          storeLocation: {
            coordinates: [lat, lng],
          },
        },
        errors: {
          ...prevState.errors,
          storeLocation: {
            coordinates: ["", ""],
          },
        },
      }));
    } else if (key === C.KEY_MANUAL_ADDRESS) {
      setStore((prevState) => ({
        ...prevState,
        values: {
          ...prevState.values,
          [C.KEY_MANUAL_ADDRESS]: val,
        },
        errors: {
          ...prevState.errors,
          [C.KEY_MANUAL_ADDRESS]: "",
        },
      }));
    }
  };

  const handleStoreInfo = (key, val) => {
    if (
      key === C.KEY_EMAIL ||
      key === C.KEY_PHONE ||
      key === C.KEY_IMAGE_URL ||
      key === C.KEY_TAX_RATE ||
      key === C.AVERAGE_PREP ||
      key === C.PICKUP_AVERAGE_PREP ||
      key === C.DINEIN_AVERAGE_PREP ||
      key === C.KEY_IS_TAX_INCLUSIVE ||
      key === C.KEY_CITY ||
      key === C.KEY_WHATSAPP_PHONE ||
      key === C.KEY_TIMEZONE ||
      key === "isAcceptQuickOrderForDelivery" ||
      key === "isPreOrderEnabledForDelivery" ||
      key === "isAcceptQuickOrderForPickup" ||
      key === "isPreOrderEnabledForPickup"
    ) {
      setStore((prevState) => ({
        ...prevState,
        values: {
          ...prevState.values,
          [key]: val,
        },
        errors: {
          ...prevState.errors,
          [key]: "",
        },
      }));
    } else if (key === C.KEY_DELETE_IMG_URL) {
      setStore((prevState) => ({
        ...prevState,
        values: {
          ...prevState.values,
          [key]: "",
        },
        errors: {
          ...prevState.errors,
          [key]: "",
        },
      }));
    } else {
      setStore((prevState) => ({
        ...prevState,
        values: {
          ...prevState.values,
          [C.BASE_LANGUAGE]: {
            ...prevState.values[C.BASE_LANGUAGE],
            [key]: val,
          },
        },
        errors: {
          ...prevState.errors,
          [C.BASE_LANGUAGE]: {
            ...prevState.errors[C.BASE_LANGUAGE],
            [key]: "",
          },
        },
      }));
    }
  };
  const didMount = useRef(false);

  useEffect(() => {
    if (didMount.current) {
      if (store.values.workingHours) {
        checkIsValidTimeSlots(store.values.workingHours, "workingHours");
      }
    } else {
      didMount.current = true;
    }
  }, [store.values.workingHours]);

  useEffect(() => {
    if (didMount.current) {
      if (store?.values?.workingHoursForPickup) {
        checkIsValidTimeSlots(
          store.values.workingHoursForPickup,
          "workingHoursForPickup"
        );
      }
    } else {
      didMount.current = true;
    }
  }, [store.values.workingHoursForPickup]);

  useEffect(() => {
    if (didMount.current) {
      if (store?.values?.workingHoursForDinein) {
        checkIsValidTimeSlots(
          store.values.workingHoursForDinein,
          "workingHoursForDinein"
        );
      }
    } else {
      didMount.current = true;
    }
  }, [store.values.workingHoursForDinein]);

  const checkIsValidTimeSlots = (workingHours, type) => {
    let slotCheck = isValidSlots(workingHours);
    if (!slotCheck.isFormValid) {
      setStore((prevState) => ({
        ...prevState,
        values: {
          ...prevState.values,
        },
        errors: {
          ...prevState.errors,
          [type]: {
            ...prevState.errors[type],
            ...slotCheck.errors,
          },
        },
      }));
    } else {
      setStore((prevState) => ({
        ...prevState,
        errors: {
          ...prevState.errors,
          [type]: {},
        },
      }));
    }
    return slotCheck.isFormValid;
  };

  const validateInputs = () => {
    let objValid = STOTE_UTILS.VALIDATE_INPUTS(store);
    if (!objValid.isFormValid) {
      setStore((prevState) => ({
        ...prevState,
        values: {
          ...prevState.values,
        },
        errors: {
          ...prevState.errors,
          ...objValid.errors,
        },
      }));
    }
    return objValid.isFormValid;
  };

  const getStoreInformation = (s) => {
    if (!s || !s?.store || !Array.isArray(s?.store) || s?.store?.length < 1) {
      logToSentry(`Invalid store data ${store}${s}`, "error");
      return null;
    }
    const obj = { ...store, values: s.store[0] };
    return obj;
  };

  const resolvedStoreId = storeId ||
    location?.state?.storeId ||
    new URLSearchParams(location.search || "").get("storeId");

  useEffect(() => {
    if (resolvedStoreId) getStoreInfo(resolvedStoreId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedStoreId]);

  const updateStoreFunction = async () => {
    try {
      store.values.mobileNumber = await UTILS.formatPhoneNumber(
        store.values.mobileNumber
      );
      store.values.whatsAppContact = await UTILS.formatPhoneNumber(
        store.values.whatsAppContact
      );
      setLoading(true);
      const userDetail = UTILS.getCurrentUser();
      let s = { ...store.values };
      const storeobj = UTILS.getStoreObj(store.values);
      storeobj.storeId = s._id;
      storeobj.brandId = userDetail.brandId;
      storeobj.currency = s.currency;
      storeobj.language = currentLanguage || "EN";
      const result = await API.updateStore({
        token: userDetail.token,
        store: storeobj,
      });
      if (result?.success === 1) {
        UTILS.successNotification(t("Store Updated Successfully"));
        as === "page" ? router.goBack() : setCurrentMode("list");
      } else {
        UTILS.errorNotification(errorHandler(result));
      }
      dispatch({ type: ACTIONS.STORES_CHANGED });
      setLoading(false);
    } catch (error) {
      UTILS.errorNotification(errorHandler(error));
      setLoading(false);
    }
  };

  const isValidTiming = () => {
    if (
      !checkIsValidTimeSlots(store?.values?.workingHours, "workingHours") &&
      store.values[C.ISDELIVERYENABLED]
    ) {
      UTILS.errorNotification(
        t("Please check timing in section", {
          serviceType: t("Delivery"),
        })
      );
      return false;
    }

    if (
      !checkIsValidTimeSlots(
        store.values?.workingHoursForDinein,
        "workingHoursForDinein"
      ) &&
      store.values[C.ISDINEINENABLED]
    ) {
      UTILS.errorNotification(
        t("Please check timing in section", {
          serviceType: t("Contactless dining"),
        })
      );
      return false;
    }

    if (
      !checkIsValidTimeSlots(
        store?.values?.workingHoursForPickup,
        "workingHoursForPickup"
      ) &&
      store.values[C.ISPICKUPENABLED]
    ) {
      UTILS.errorNotification(
        t("Please check timing in section", {
          serviceType: t("Pickup"),
        })
      );
      return false;
    }
    return true;
  };

  const updateStore = async () => {
    if (!validateInputs()) {
    } else if (!isValidTiming()) {
      return;
    } else {
      updateStoreFunction();
    }
  };

  return (
    <AppContainer
      title={title}
      subTitle={subTitle}
      loading={loading}
      footer={
        <div className="flex justify-end gap-2">
          <AppOutlineButton
            variant="secondary"
            onClick={() => {
              as === "page" ? router.goBack() : setCurrentMode("list");
            }}
            disabled={loading}
          >
            {t("Cancel")}
          </AppOutlineButton>
          <AppButton onClick={updateStore} loading={loading}>
            {t("Update Store")}
          </AppButton>
        </div>
      }
    >
      <div className="flex w-full flex-col gap-0">
        {/* <BusinessInfo defaultOpen={true} /> */}
        <BasicInfo store={store} handleValues={handleStoreInfo} type="update" defaultOpen={true} />
        <Location
          store={store}
          setStore={setStore}
          handleValues={handleStoreMapSection}
          type="update"
          zoom={21}
        />
        <Delivery
          store={store}
          setStore={setStore}
          addStore={updateStore}
          type="update"
        />
        <Pickup
          store={store}
          setStore={setStore}
          addStore={updateStore}
          handleStoreInfo={handleStoreInfo}
          type="update"
        />
        <Dinein
          store={store}
          setStore={setStore}
          addStore={updateStore}
          handleStoreInfo={handleStoreInfo}
          type="update"
        />
      </div>
    </AppContainer>
    
  );
};
export default UpdateStoreComponent;

UpdateStoreComponent.propTypes = {
  values: PropTypes.object,
};
